import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getLanguages,
  addLanguage as addLang,
  updateLanguage as updateLang,
  deleteLanguage as deleteLang,
} from "../../services/languageService";
import { toast } from "react-toastify";

const initialState = {
  languages: [],
  status: "idle", // "idle" | "loading" | "succeeded" | "failed"
  error: null,
};

// Fetch all languages
export const fetchLanguages = createAsyncThunk(
  "languages/fetchLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getLanguages();
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(error.response?.data?.message || "An error occurred");
    }
  }
);

// Add a new language
export const addLanguage = createAsyncThunk(
  "languages/addLanguage",
  async (language, { rejectWithValue }) => {
    try {
      const response = await addLang(language);
      toast.success(response.message || "Data added successfully!");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(error.response?.data?.message || "An error occurred");
    }
  }
);

// Update a language
export const updateLanguage = createAsyncThunk(
  "languages/updateLanguage",
  async ({ id, language }, { rejectWithValue }) => {
    try {
      const response = await updateLang(id, language);
      toast.success(response.message || "Data updated successfully!");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(error.response?.data?.message || "An error occurred");
    }
  }
);

// Delete a language
export const deleteLanguage = createAsyncThunk(
  "languages/deleteLanguage",
  async (id, { rejectWithValue }) => {
    try {
      let response = await deleteLang(id);
     toast.success(response.message || "Data deleted successfully!");
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(error.response?.data?.message || "An error occurred");
    }
  }
);

const languageSlice = createSlice({
  name: "languages",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Languages
      .addCase(fetchLanguages.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.languages = action.payload.data;
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Add Language
      .addCase(addLanguage.fulfilled, (state, action) => {
        state.languages.unshift(action.payload.data); // Add at the beginning
      })

      // Update Language
      .addCase(updateLanguage.fulfilled, (state, action) => {
        const updatedLanguage = action.payload.data;

        // Ensure `state.languages` exists and is an array
        if (!Array.isArray(state.languages)) {
            return;
        }
        const index = state.languages.findIndex((lang) =>{
         return lang._id === updatedLanguage._id
        });
        if (index !== -1) {
          state.languages[index] = { ...state.languages[index], ...updatedLanguage };
        }
      })

      // Delete Language
      .addCase(deleteLanguage.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.languages = state.languages.filter((lang) => lang._id !== deletedId);
      });
  },
});

export const { clearError } = languageSlice.actions;
export default languageSlice.reducer;
